import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Check, 
  Lock, 
  Download, 
  ArrowRight,
  ArrowLeft,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Trophy,
  BookOpen,
  ClipboardList,
  PenTool,
  Eraser
} from 'lucide-react';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_TERRACOTTA = "https://customer-assets.emergentagent.com/job_academy-session/artifacts/gtms0r7v_Logo%20Agence%20Immobilier%20Minimaliste%20Marron%20Gris%20Beige%20-%203.PNG";

export default function StudentPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Questionnaire state
  const [activeQuestionnaire, setActiveQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  
  // QCM state
  const [activeQCM, setActiveQCM] = useState(null);
  const [qcmQuestions, setQcmQuestions] = useState([]);
  const [qcmAnswers, setQcmAnswers] = useState([]);
  const [qcmCurrentIndex, setQcmCurrentIndex] = useState(0);
  const [qcmResult, setQcmResult] = useState(null);
  const [showQcmResults, setShowQcmResults] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Contract state
  const [showContractView, setShowContractView] = useState(false);
  const [contractPreview, setContractPreview] = useState(null);
  const [contractForm, setContractForm] = useState({
    entreprise: '', siret: '', adresse: '', is_particulier: false
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    fetchStudentData();
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await axios.get(`${API}/payment/stripe-status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            toast.success('Paiement confirmé ! Merci pour votre inscription.');
            fetchStudentData();
            return;
          }
          if (res.data.status === 'expired') {
            toast.error('La session de paiement a expiré. Merci de réessayer.');
            return;
          }
        } catch (e) { /* ignore */ }
        if (attempts < 6) {
          setTimeout(poll, 2500);
        } else {
          toast.info('Paiement en cours de vérification. Cela peut prendre quelques instants.');
        }
      };
      poll();
    }
  }, [token]);

  const fetchStudentData = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/student/${token}`);
      setData(res.data);
    } catch (error) {
      setError('Lien invalide ou expiré');
    } finally {
      setIsLoading(false);
    }
  };

  const isQuestionnaireCompleted = (questionnaireType) => {
    if (!data) return false;
    return questionnaireType === 'milieu' 
      ? data.participant.questionnaire_milieu_completed 
      : data.participant.questionnaire_fin_completed;
  };

  const isQCMCompleted = (day) => {
    if (!data) return false;
    return day === 1 
      ? data.participant.qcm_jour1_completed 
      : data.participant.qcm_jour2_completed;
  };

  const getQCMScore = (day) => {
    if (!data) return null;
    return day === 1 
      ? data.participant.qcm_jour1_score 
      : data.participant.qcm_jour2_score;
  };

  const canAccessQuestionnaire = (questionnaireType) => {
    if (questionnaireType === 'milieu') return true;
    return data?.participant?.questionnaire_milieu_completed;
  };

  const canDownloadCertificate = () => {
    return data?.participant?.questionnaire_milieu_completed && 
           data?.participant?.questionnaire_fin_completed &&
           data?.participant?.qcm_jour1_completed &&
           data?.participant?.qcm_jour2_completed;
  };

  // Questionnaire handlers
  const openQuestionnaire = (questionnaire) => {
    if (!canAccessQuestionnaire(questionnaire.type)) {
      toast.error('Vous devez d\'abord compléter le questionnaire de mi-parcours');
      return;
    }
    
    const existingAnswers = data.answers.find(a => a.questionnaire_id === questionnaire.id);
    if (existingAnswers) {
      const answerMap = {};
      existingAnswers.answers.forEach(a => {
        answerMap[a.question_id] = a.answer;
      });
      setAnswers(answerMap);
    } else {
      setAnswers({});
    }
    
    setActiveQuestionnaire(questionnaire);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleSubmitQuestionnaire = async () => {
    if (!activeQuestionnaire) return;

    const unanswered = activeQuestionnaire.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error('Veuillez répondre à toutes les questions');
      return;
    }

    setIsSubmitting(true);
    try {
      const answerPayload = {
        questionnaire_id: activeQuestionnaire.id,
        answers: Object.entries(answers).map(([question_id, answer]) => ({
          question_id,
          answer
        }))
      };

      await axios.post(`${API}/answers?token=${token}`, answerPayload);
      toast.success('Réponses enregistrées');
      setActiveQuestionnaire(null);
      fetchStudentData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  // QCM handlers
  const openQCM = async (qcmInfo) => {
    try {
      const res = await axios.get(`${API}/qcm/${qcmInfo.id}`);
      setQcmQuestions(res.data.questions);
      setQcmAnswers(new Array(res.data.questions.length).fill(''));
      setQcmCurrentIndex(0);
      setQcmResult(null);
      setShowQcmResults(false);
      setActiveQCM(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du QCM');
    }
  };

  const handleQCMAnswer = (letter) => {
    const newAnswers = [...qcmAnswers];
    newAnswers[qcmCurrentIndex] = letter;
    setQcmAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (qcmCurrentIndex < qcmQuestions.length - 1) {
      setQcmCurrentIndex(qcmCurrentIndex + 1);
    }
  };

  const prevQuestion = () => {
    if (qcmCurrentIndex > 0) {
      setQcmCurrentIndex(qcmCurrentIndex - 1);
    }
  };

  const handleSubmitQCM = async () => {
    const unanswered = qcmAnswers.filter(a => !a).length;
    if (unanswered > 0) {
      toast.error(`Il reste ${unanswered} question(s) sans réponse`);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/qcm/submit?token=${token}`, {
        qcm_id: activeQCM.id,
        answers: qcmAnswers
      });
      
      setQcmResult(res.data);
      setShowQcmResults(true);
      fetchStudentData();
      
      if (res.data.passed) {
        toast.success(`Bravo ! Vous avez obtenu ${res.data.score_percent}%`);
      } else {
        toast.info(`Score: ${res.data.score_percent}%. Minimum requis: 70%`);
      }
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeQCM = () => {
    setActiveQCM(null);
    setQcmResult(null);
    setShowQcmResults(false);
  };

  const downloadCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificate/${token}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attestation_${data.participant.prenom.toLowerCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Attestation téléchargée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    }
  };

  // Contract handlers
  const openContractView = async () => {
    try {
      const res = await axios.get(`${API}/contract/${token}/preview`);
      setContractPreview(res.data);
      setContractForm(prev => ({
        ...prev,
        entreprise: res.data.participant.reseau || '',
      }));
      setShowContractView(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du contrat');
    }
  };

  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#2C1F1A';
    ctx.lineWidth = 2;
    ctxRef.current = ctx;
    
    // Clear canvas with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const isCanvasBlank = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i] !== 255 || pixels[i + 1] !== 255 || pixels[i + 2] !== 255) {
        return false;
      }
    }
    return true;
  };

  const handleSignContract = async () => {
    if (isCanvasBlank()) {
      toast.error('Veuillez signer dans la zone de signature');
      return;
    }
    if (!contractForm.is_particulier && !contractForm.entreprise) {
      toast.error('Veuillez renseigner le nom de votre entreprise');
      return;
    }
    if (!contractForm.adresse) {
      toast.error('Veuillez renseigner votre adresse');
      return;
    }

    setIsSubmitting(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      
      await axios.post(`${API}/contract/${token}/sign`, {
        signature_data: signatureData,
        entreprise: contractForm.entreprise,
        siret: contractForm.siret,
        adresse: contractForm.adresse,
        is_particulier: contractForm.is_particulier,
      });
      
      toast.success('Convention signée avec succès !');
      setShowContractView(false);
      fetchStudentData();
    } catch (error) {
      toast.error('Erreur lors de la signature');
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadSignedContract = async () => {
    try {
      const response = await axios.get(`${API}/contract/${token}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Convention_${data.participant.prenom}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Convention téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-body text-brown-mid">Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-8">
        <div className="text-center">
          <Lock size={48} className="text-brown-light mx-auto mb-4" />
          <h1 className="font-display text-2xl text-brown mb-2">Accès non autorisé</h1>
          <p className="font-body text-brown-mid">{error}</p>
          <a href="/" className="inline-block mt-6 text-terra hover:underline font-body">
            Retour à l&apos;accueil
          </a>
        </div>
      </div>
    );
  }

  // Contract Signing View
  if (showContractView && contractPreview) {
    const session = contractPreview.session;
    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-white border-b border-sand px-8 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <img src={LOGO_TERRACOTTA} alt="L'Académie" className="h-8" />
            <button
              onClick={() => setShowContractView(false)}
              className="font-body text-sm text-brown-mid hover:text-brown"
            >
              ← Retour au tableau de bord
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-8 py-12">
          <h1 className="font-display text-3xl text-brown mb-2">Convention de Formation</h1>
          <p className="font-body text-sm text-brown-mid mb-8">
            Veuillez lire et signer la convention de formation ci-dessous.
          </p>

          {/* Contract Content */}
          <div className="bg-white border border-sand p-8 mb-6" data-testid="contract-content">
            <h2 className="font-display text-xl text-brown text-center mb-1">CONVENTION DE FORMATION PROFESSIONNELLE</h2>
            <p className="font-body text-xs text-brown-light text-center mb-6">(Articles L.6353-1 à L.6353-9 du Code du Travail)</p>

            <div className="space-y-4 font-body text-sm text-brown">
              <div className="border-l-2 border-terra pl-4">
                <p className="font-semibold text-terra mb-1">L&apos;ORGANISME DE FORMATION :</p>
                <p>L&apos;Académie de l&apos;Immobilier Commercial</p>
                <p className="text-xs text-brown-mid">N° SIRET : 93282508700016 · N° déclaration : 75331467133</p>
                <p className="text-xs text-brown-mid">Représentée par Nadège Ledru, Directrice de formation</p>
              </div>

              <div className="border-l-2 border-brown pl-4">
                <p className="font-semibold mb-1">LE STAGIAIRE :</p>
                <p>{contractPreview.participant.prenom} — {contractPreview.participant.email}</p>
              </div>

              <div className="pt-2">
                <p className="font-semibold text-terra">Article 1 — Objet</p>
                <p className="text-sm">Formation professionnelle « Immobilier Commercial : Les Fondamentaux » dispensée par L&apos;Académie de l&apos;Immobilier Commercial.</p>
              </div>
              <div>
                <p className="font-semibold text-terra">Article 2 — Nature et objectifs</p>
                <p className="text-sm">Fournir les compétences nécessaires pour exercer dans l&apos;immobilier commercial : bail commercial, cession de fonds de commerce, murs commerciaux, prospection terrain.</p>
              </div>
              <div>
                <p className="font-semibold text-terra">Article 3 — Durée et dates</p>
                <p className="text-sm">
                  {session ? (
                    <>Formation sur 2 jours : Jour 1 le {new Date(session.date_jour1 + 'T00:00:00').toLocaleDateString('fr-FR', {day: 'numeric', month: 'long', year: 'numeric'})}, Jour 2 le {new Date(session.date_jour2 + 'T00:00:00').toLocaleDateString('fr-FR', {day: 'numeric', month: 'long', year: 'numeric'})} · {session.heure_debut} — {session.heure_fin} · {session.lieu}</>
                  ) : 'Dates à confirmer'}
                </p>
              </div>
              <div>
                <p className="font-semibold text-terra">Article 4 — Tarif</p>
                <p className="text-sm">{session ? session.tarif : 300} € TTC, payable à l&apos;inscription.</p>
              </div>
              <div>
                <p className="font-semibold text-terra">Article 5 — Moyens pédagogiques</p>
                <p className="text-sm">Formation interactive via visioconférence, supports numériques, cas pratiques et QCM d&apos;évaluation.</p>
              </div>
              <div>
                <p className="font-semibold text-terra">Article 6 — Évaluation</p>
                <p className="text-sm">QCM en fin de Jour 1 et Jour 2. Attestation de fin de formation délivrée à l&apos;issue du parcours complet.</p>
              </div>
              <div>
                <p className="font-semibold text-terra">Article 7 — Délai de rétractation</p>
                <p className="text-sm">Le stagiaire dispose d&apos;un délai de rétractation de 14 jours à compter de la signature.</p>
              </div>
            </div>
          </div>

          {/* Participant Info Form */}
          <div className="bg-white border border-sand p-6 mb-6" data-testid="contract-form">
            <h3 className="font-display text-lg text-brown mb-4">Vos informations</h3>
            
            <div className="mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={contractForm.is_particulier}
                  onChange={(e) => setContractForm({...contractForm, is_particulier: e.target.checked})}
                  className="accent-terra w-4 h-4"
                  data-testid="checkbox-particulier"
                />
                <span className="font-body text-sm text-brown">Je suis un particulier (pas d&apos;entreprise)</span>
              </label>
            </div>

            {!contractForm.is_particulier && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="form-label block mb-2">ENTREPRISE / SOCIÉTÉ</label>
                  <input
                    type="text"
                    className="form-input w-full"
                    value={contractForm.entreprise}
                    onChange={(e) => setContractForm({...contractForm, entreprise: e.target.value})}
                    placeholder="Nom de l'entreprise"
                    data-testid="input-entreprise"
                  />
                </div>
                <div>
                  <label className="form-label block mb-2">N° SIRET</label>
                  <input
                    type="text"
                    className="form-input w-full"
                    value={contractForm.siret}
                    onChange={(e) => setContractForm({...contractForm, siret: e.target.value})}
                    placeholder="Ex: 123 456 789 00012"
                    data-testid="input-siret"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="form-label block mb-2">ADRESSE</label>
              <input
                type="text"
                className="form-input w-full"
                value={contractForm.adresse}
                onChange={(e) => setContractForm({...contractForm, adresse: e.target.value})}
                placeholder="Adresse complète"
                data-testid="input-adresse"
              />
            </div>
          </div>

          {/* Signature Pad */}
          <div className="bg-white border border-sand p-6 mb-6" data-testid="signature-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-brown flex items-center gap-2">
                <PenTool size={18} className="text-terra" />
                Votre signature
              </h3>
              <button
                onClick={clearSignature}
                className="flex items-center gap-1 text-brown-mid hover:text-brown font-body text-sm"
                data-testid="clear-signature"
              >
                <Eraser size={14} />
                Effacer
              </button>
            </div>
            <div className="border-2 border-dashed border-sand bg-white cursor-crosshair">
              <canvas
                ref={initCanvas}
                width={700}
                height={200}
                className="w-full touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
            </div>
            <p className="font-body text-xs text-brown-light mt-2">Dessinez votre signature ci-dessus avec la souris ou le doigt</p>
          </div>

          {/* Sign Button */}
          <button
            onClick={handleSignContract}
            disabled={isSubmitting}
            className="cta-button w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="sign-contract-button"
          >
            <PenTool size={16} />
            {isSubmitting ? 'Signature en cours...' : 'Signer la convention'}
          </button>
        </main>
      </div>
    );
  }

  // QCM Results View
  if (showQcmResults && qcmResult) {
    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-white border-b border-sand px-8 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <img src={LOGO_TERRACOTTA} alt="L'Académie" className="h-8" />
            <button
              onClick={closeQCM}
              className="font-body text-sm text-brown-mid hover:text-brown"
            >
              ← Retour au tableau de bord
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-8 py-12">
          {/* Score Card */}
          <div className={`text-center p-8 mb-8 ${qcmResult.passed ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className={`w-20 h-20 mx-auto mb-4 flex items-center justify-center ${qcmResult.passed ? 'bg-green-100' : 'bg-yellow-100'}`}>
              {qcmResult.passed ? (
                <Trophy size={40} className="text-green-600" />
              ) : (
                <BookOpen size={40} className="text-yellow-600" />
              )}
            </div>
            <h1 className="font-display text-3xl text-brown mb-2">
              {qcmResult.passed ? 'Félicitations !' : 'Continuez vos efforts !'}
            </h1>
            <p className="font-display text-5xl font-semibold text-terra mb-2">
              {qcmResult.score_percent}%
            </p>
            <p className="font-body text-brown-mid">
              {qcmResult.score} bonnes réponses sur {qcmResult.total}
            </p>
            {!qcmResult.passed && (
              <p className="font-body text-sm text-yellow-700 mt-4">
                Le score minimum requis est de 70%. Vous pouvez repasser le QCM.
              </p>
            )}
          </div>

          {/* Results Detail */}
          <h2 className="font-display text-xl text-brown mb-4">Détail des réponses</h2>
          <div className="space-y-4">
            {qcmResult.results.map((result, i) => (
              <div 
                key={i} 
                className={`p-4 border ${result.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${result.is_correct ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {result.is_correct ? <CheckCircle size={18} /> : <XCircle size={18} />}
                  </div>
                  <div className="flex-1">
                    <p className="font-body text-sm text-brown mb-2">
                      <strong>Q{i + 1}.</strong> {qcmQuestions[i]?.text}
                    </p>
                    <div className="flex gap-4 text-sm">
                      <span className={result.is_correct ? 'text-green-700' : 'text-red-700'}>
                        Votre réponse: <strong>{result.user_answer}</strong>
                      </span>
                      {!result.is_correct && (
                        <span className="text-green-700">
                          Bonne réponse: <strong>{result.correct_answer}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={closeQCM}
            className="cta-button w-full mt-8"
          >
            Retour au tableau de bord
          </button>
        </main>
      </div>
    );
  }

  // Active QCM View
  if (activeQCM && qcmQuestions.length > 0) {
    const currentQuestion = qcmQuestions[qcmCurrentIndex];
    const progress = ((qcmCurrentIndex + 1) / qcmQuestions.length) * 100;
    const answeredCount = qcmAnswers.filter(a => a).length;

    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-white border-b border-sand px-8 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <img src={LOGO_TERRACOTTA} alt="L'Académie" className="h-8" />
            <button
              onClick={() => setActiveQCM(null)}
              className="font-body text-sm text-brown-mid hover:text-brown"
            >
              ← Quitter le QCM
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-8 py-12">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-sm text-brown-mid">
                Question {qcmCurrentIndex + 1} sur {qcmQuestions.length}
              </span>
              <span className="font-body text-sm text-terra">
                {answeredCount} répondue(s)
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <div className="bg-white border border-sand p-8 mb-6">
            <div className="flex items-start gap-4 mb-6">
              <span className="font-display text-3xl text-terra font-semibold">
                {qcmCurrentIndex + 1}
              </span>
              <p className="font-body text-lg text-brown leading-relaxed">
                {currentQuestion.text}
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, i) => {
                const letter = ['A', 'B', 'C', 'D'][i];
                const isSelected = qcmAnswers[qcmCurrentIndex] === letter;
                
                return (
                  <button
                    key={i}
                    onClick={() => handleQCMAnswer(letter)}
                    className={`w-full p-4 text-left border transition-all flex items-center gap-4 ${
                      isSelected
                        ? 'bg-terra/10 border-terra text-brown'
                        : 'bg-cream border-sand text-brown-mid hover:border-terra'
                    }`}
                    data-testid={`qcm-option-${letter}`}
                  >
                    <span className={`w-10 h-10 flex items-center justify-center border ${
                      isSelected ? 'bg-terra border-terra text-white' : 'border-sand text-brown-mid'
                    }`}>
                      <span className="font-display text-lg">{letter}</span>
                    </span>
                    <span className="font-body text-sm flex-1">{option}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={prevQuestion}
              disabled={qcmCurrentIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-brown-mid hover:text-brown disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={18} />
              Précédent
            </button>

            {qcmCurrentIndex === qcmQuestions.length - 1 ? (
              <button
                onClick={handleSubmitQCM}
                disabled={isSubmitting}
                className="cta-button flex items-center gap-2 disabled:opacity-50"
                data-testid="submit-qcm"
              >
                {isSubmitting ? 'Envoi...' : 'Terminer le QCM'}
                <CheckCircle size={18} />
              </button>
            ) : (
              <button
                onClick={nextQuestion}
                className="cta-button flex items-center gap-2"
              >
                Suivant
                <ArrowRight size={18} />
              </button>
            )}
          </div>

          {/* Question Navigator */}
          <div className="mt-8 p-4 bg-white border border-sand">
            <p className="font-body text-xs text-brown-mid mb-3">Navigation rapide</p>
            <div className="flex flex-wrap gap-2">
              {qcmQuestions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setQcmCurrentIndex(i)}
                  className={`w-8 h-8 flex items-center justify-center text-sm ${
                    i === qcmCurrentIndex
                      ? 'bg-brown text-cream'
                      : qcmAnswers[i]
                        ? 'bg-terra/20 text-terra border border-terra'
                        : 'bg-cream border border-sand text-brown-mid'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Active Questionnaire View
  if (activeQuestionnaire) {
    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-white border-b border-sand px-8 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <img src={LOGO_TERRACOTTA} alt="L'Académie" className="h-8" />
            <button
              onClick={() => setActiveQuestionnaire(null)}
              className="font-body text-sm text-brown-mid hover:text-brown"
              data-testid="back-button"
            >
              ← Retour
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-8 py-12">
          <div className="mb-8">
            <span className={`inline-block px-2 py-1 text-xs font-body uppercase tracking-wider ${
              activeQuestionnaire.type === 'milieu' ? 'bg-terra/10 text-terra' : 'bg-brown/10 text-brown'
            }`}>
              {activeQuestionnaire.type === 'milieu' ? 'Mi-parcours' : 'Fin de formation'}
            </span>
            <h1 className="font-display text-3xl text-brown mt-3">{activeQuestionnaire.title}</h1>
            <p className="font-body text-brown-mid mt-2">
              Veuillez répondre à toutes les questions ci-dessous.
            </p>
          </div>

          <div className="space-y-6">
            {activeQuestionnaire.questions.map((question, index) => (
              <div key={question.id} className="bg-white border border-sand p-6" data-testid={`question-card-${index}`}>
                <div className="flex gap-4">
                  <span className="font-display text-2xl text-terra font-semibold">{index + 1}</span>
                  <div className="flex-1">
                    <p className="font-body text-brown mb-4">{question.text}</p>
                    
                    {question.type === 'text' && (
                      <textarea
                        className="form-input w-full min-h-[100px]"
                        value={answers[question.id] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder="Votre réponse..."
                        data-testid={`answer-text-${index}`}
                      />
                    )}

                    {question.type === 'rating' && (
                      <div className="flex gap-3">
                        {[1, 2, 3, 4, 5].map((value) => (
                          <button
                            key={value}
                            onClick={() => handleAnswerChange(question.id, value.toString())}
                            className={`w-12 h-12 border transition-colors ${
                              answers[question.id] === value.toString()
                                ? 'bg-terra border-terra text-white'
                                : 'bg-cream border-sand text-brown hover:border-terra'
                            }`}
                            data-testid={`rating-${index}-${value}`}
                          >
                            <span className="font-display text-lg">{value}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {question.type === 'choice' && question.options && (
                      <div className="space-y-2">
                        {question.options.map((option, optIndex) => (
                          <button
                            key={optIndex}
                            onClick={() => handleAnswerChange(question.id, option)}
                            className={`w-full p-3 text-left border transition-colors ${
                              answers[question.id] === option
                                ? 'bg-terra/10 border-terra text-brown'
                                : 'bg-cream border-sand text-brown-mid hover:border-terra'
                            }`}
                            data-testid={`choice-${index}-${optIndex}`}
                          >
                            <span className="font-body text-sm">{option}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleSubmitQuestionnaire}
            disabled={isSubmitting}
            className="cta-button w-full mt-8 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="submit-questionnaire-button"
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer mes réponses'}
            <ArrowRight size={16} />
          </button>
        </main>
      </div>
    );
  }

  // Calculate progress
  const progressSteps = [
    data.participant.contract_status === 'signed',
    data.participant.qcm_jour1_completed,
    data.participant.questionnaire_milieu_completed,
    data.participant.qcm_jour2_completed,
    data.participant.questionnaire_fin_completed
  ];
  const completedSteps = progressSteps.filter(Boolean).length;
  const progressPercent = (completedSteps / progressSteps.length) * 100;

  // Main student portal view
  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-white border-b border-sand px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={LOGO_TERRACOTTA} alt="L'Académie" className="h-8" data-testid="student-logo" />
          <span className="font-body text-sm text-brown-mid">
            Espace participant
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-8 py-12">
        {/* Welcome */}
        <div className="mb-12">
          <h1 className="font-display text-4xl text-brown mb-2">
            Bonjour, <em className="italic text-terra">{data.participant.prenom}</em>
          </h1>
          <p className="font-body text-brown-mid">
            Bienvenue dans votre espace de suivi de formation.
          </p>
        </div>

        {/* Progress Overview */}
        <div className="bg-white border border-sand p-6 mb-8" data-testid="progress-card">
          <h2 className="font-display text-xl text-brown mb-4">Votre progression</h2>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <Progress value={progressPercent} className="h-3" />
            </div>
            <span className="font-display text-xl text-terra font-semibold">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <div className="grid grid-cols-5 gap-4">
            <div className="text-center">
              <div className={`w-10 h-10 mx-auto flex items-center justify-center ${data.participant.contract_status === 'signed' ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                {data.participant.contract_status === 'signed' ? <CheckCircle size={20} /> : <PenTool size={20} />}
              </div>
              <p className="font-body text-xs text-brown-mid mt-2">Convention</p>
            </div>
            <div className="text-center">
              <div className={`w-10 h-10 mx-auto flex items-center justify-center ${data.participant.qcm_jour1_completed ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                {data.participant.qcm_jour1_completed ? <CheckCircle size={20} /> : <ClipboardList size={20} />}
              </div>
              <p className="font-body text-xs text-brown-mid mt-2">QCM J1</p>
              {data.participant.qcm_jour1_score !== null && (
                <p className="font-display text-sm text-terra font-semibold">{data.participant.qcm_jour1_score}%</p>
              )}
            </div>
            <div className="text-center">
              <div className={`w-10 h-10 mx-auto flex items-center justify-center ${data.participant.questionnaire_milieu_completed ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                {data.participant.questionnaire_milieu_completed ? <CheckCircle size={20} /> : <FileText size={20} />}
              </div>
              <p className="font-body text-xs text-brown-mid mt-2">Éval. J1</p>
            </div>
            <div className="text-center">
              <div className={`w-10 h-10 mx-auto flex items-center justify-center ${data.participant.qcm_jour2_completed ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                {data.participant.qcm_jour2_completed ? <CheckCircle size={20} /> : <ClipboardList size={20} />}
              </div>
              <p className="font-body text-xs text-brown-mid mt-2">QCM J2</p>
              {data.participant.qcm_jour2_score !== null && (
                <p className="font-display text-sm text-terra font-semibold">{data.participant.qcm_jour2_score}%</p>
              )}
            </div>
            <div className="text-center">
              <div className={`w-10 h-10 mx-auto flex items-center justify-center ${data.participant.questionnaire_fin_completed ? 'bg-green-100 text-green-600' : 'bg-sand text-brown-light'}`}>
                {data.participant.questionnaire_fin_completed ? <CheckCircle size={20} /> : <FileText size={20} />}
              </div>
              <p className="font-body text-xs text-brown-mid mt-2">Éval. Fin</p>
            </div>
          </div>
        </div>

        {/* Contract Signing */}
        <div className={`bg-white border ${data.participant.contract_status === 'signed' ? 'border-green-200' : 'border-terra'} p-6 mb-8`} data-testid="contract-card">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${data.participant.contract_status === 'signed' ? 'bg-green-100 border border-green-200' : 'bg-terra/10 border border-terra'}`}>
              {data.participant.contract_status === 'signed' 
                ? <CheckCircle size={24} className="text-green-600" />
                : <PenTool size={24} className="text-terra" />
              }
            </div>
            <div className="flex-1">
              <h3 className="font-display text-xl text-brown mb-2">Convention de formation</h3>
              {data.participant.contract_status === 'signed' ? (
                <>
                  <p className="font-body text-sm text-green-700 mb-4">
                    Convention signée le {data.participant.contract_signed_at ? new Date(data.participant.contract_signed_at).toLocaleDateString('fr-FR', {day: 'numeric', month: 'long', year: 'numeric'}) : ''}
                  </p>
                  <button
                    onClick={downloadSignedContract}
                    className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-700 font-body text-sm hover:bg-green-100 transition-colors"
                    data-testid="download-contract-button"
                  >
                    <Download size={16} />
                    Télécharger la convention signée
                  </button>
                </>
              ) : (
                <>
                  <p className="font-body text-sm text-brown-mid mb-4">
                    Avant de commencer votre formation, veuillez lire et signer votre convention de formation professionnelle.
                  </p>
                  <button
                    onClick={openContractView}
                    className="flex items-center gap-2 px-6 py-3 bg-terra text-white font-body text-sm hover:bg-terra-dark transition-colors"
                    data-testid="sign-contract-cta"
                  >
                    <PenTool size={16} />
                    Signer la convention
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Parcours Jour 1 */}
        <div className="mb-8">
          <h2 className="font-display text-xl text-brown mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-terra text-white flex items-center justify-center font-display text-lg">1</span>
            Jour 1 - Bail commercial & Fonds de commerce
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* QCM Jour 1 */}
            {data.qcm_list && data.qcm_list.find(q => q.day === 1) && (
              <div className="bg-white border border-sand p-6" data-testid="qcm-jour1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block px-2 py-1 text-xs font-body uppercase tracking-wider bg-terra/10 text-terra mb-2">
                      QCM · 20 questions
                    </span>
                    <h3 className="font-display text-lg text-brown">QCM Jour 1</h3>
                  </div>
                  {isQCMCompleted(1) ? (
                    <div className="text-right">
                      <CheckCircle size={24} className="text-green-500 ml-auto" />
                      <span className="font-display text-lg text-terra font-semibold">{getQCMScore(1)}%</span>
                    </div>
                  ) : (
                    <Clock size={24} className="text-terra" />
                  )}
                </div>
                
                <p className="font-body text-sm text-brown-mid mb-4">
                  Testez vos connaissances sur le bail commercial, le fonds de commerce et le droit au bail.
                </p>

                <button
                  onClick={() => openQCM(data.qcm_list.find(q => q.day === 1))}
                  className={`w-full py-3 font-body text-sm transition-colors ${
                    isQCMCompleted(1) 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-terra text-white hover:bg-terra-dark'
                  }`}
                  data-testid="start-qcm-jour1"
                >
                  {isQCMCompleted(1) ? 'Repasser le QCM' : 'Commencer le QCM'}
                </button>
              </div>
            )}

            {/* Questionnaire Mi-parcours */}
            {data.questionnaires.find(q => q.type === 'milieu') && (
              <div className="bg-white border border-sand p-6" data-testid="questionnaire-milieu">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block px-2 py-1 text-xs font-body uppercase tracking-wider bg-brown/10 text-brown mb-2">
                      Évaluation
                    </span>
                    <h3 className="font-display text-lg text-brown">
                      {data.questionnaires.find(q => q.type === 'milieu').title}
                    </h3>
                  </div>
                  {isQuestionnaireCompleted('milieu') ? (
                    <CheckCircle size={24} className="text-green-500" />
                  ) : (
                    <Clock size={24} className="text-brown-light" />
                  )}
                </div>
                
                <p className="font-body text-sm text-brown-mid mb-4">
                  Évaluation de mi-parcours pour adapter la suite de la formation.
                </p>

                <button
                  onClick={() => openQuestionnaire(data.questionnaires.find(q => q.type === 'milieu'))}
                  className={`w-full py-3 font-body text-sm transition-colors ${
                    isQuestionnaireCompleted('milieu') 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-brown text-cream hover:bg-brown/90'
                  }`}
                >
                  {isQuestionnaireCompleted('milieu') ? 'Voir / Modifier' : 'Répondre'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Parcours Jour 2 */}
        <div className="mb-8">
          <h2 className="font-display text-xl text-brown mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-brown text-cream flex items-center justify-center font-display text-lg">2</span>
            Jour 2 - Murs commerciaux & Prospection
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* QCM Jour 2 */}
            {data.qcm_list && data.qcm_list.find(q => q.day === 2) && (
              <div className="bg-white border border-sand p-6" data-testid="qcm-jour2">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block px-2 py-1 text-xs font-body uppercase tracking-wider bg-terra/10 text-terra mb-2">
                      QCM · 20 questions
                    </span>
                    <h3 className="font-display text-lg text-brown">QCM Jour 2</h3>
                  </div>
                  {isQCMCompleted(2) ? (
                    <div className="text-right">
                      <CheckCircle size={24} className="text-green-500 ml-auto" />
                      <span className="font-display text-lg text-terra font-semibold">{getQCMScore(2)}%</span>
                    </div>
                  ) : (
                    <Clock size={24} className="text-terra" />
                  )}
                </div>
                
                <p className="font-body text-sm text-brown-mid mb-4">
                  Testez vos connaissances sur les murs commerciaux, l&apos;emplacement et la prospection.
                </p>

                <button
                  onClick={() => openQCM(data.qcm_list.find(q => q.day === 2))}
                  className={`w-full py-3 font-body text-sm transition-colors ${
                    isQCMCompleted(2) 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-terra text-white hover:bg-terra-dark'
                  }`}
                  data-testid="start-qcm-jour2"
                >
                  {isQCMCompleted(2) ? 'Repasser le QCM' : 'Commencer le QCM'}
                </button>
              </div>
            )}

            {/* Questionnaire Fin */}
            {data.questionnaires.find(q => q.type === 'fin') && (
              <div className={`bg-white border border-sand p-6 ${!canAccessQuestionnaire('fin') ? 'opacity-60' : ''}`} data-testid="questionnaire-fin">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block px-2 py-1 text-xs font-body uppercase tracking-wider bg-brown/10 text-brown mb-2">
                      Évaluation finale
                    </span>
                    <h3 className="font-display text-lg text-brown">
                      {data.questionnaires.find(q => q.type === 'fin').title}
                    </h3>
                  </div>
                  {isQuestionnaireCompleted('fin') ? (
                    <CheckCircle size={24} className="text-green-500" />
                  ) : !canAccessQuestionnaire('fin') ? (
                    <Lock size={24} className="text-brown-light" />
                  ) : (
                    <Clock size={24} className="text-brown-light" />
                  )}
                </div>
                
                <p className="font-body text-sm text-brown-mid mb-4">
                  Évaluation finale de la formation.
                </p>

                <button
                  onClick={() => openQuestionnaire(data.questionnaires.find(q => q.type === 'fin'))}
                  disabled={!canAccessQuestionnaire('fin')}
                  className={`w-full py-3 font-body text-sm transition-colors ${
                    isQuestionnaireCompleted('fin') 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : canAccessQuestionnaire('fin')
                        ? 'bg-brown text-cream hover:bg-brown/90'
                        : 'bg-sand text-brown-light cursor-not-allowed'
                  }`}
                >
                  {isQuestionnaireCompleted('fin') ? 'Voir / Modifier' : canAccessQuestionnaire('fin') ? 'Répondre' : 'Verrouillé'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Certificate */}
        <div className={`bg-white border border-sand p-6 ${!canDownloadCertificate() ? 'opacity-60' : ''}`} data-testid="certificate-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 border border-terra flex items-center justify-center flex-shrink-0">
              <FileText size={24} className="text-terra" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-xl text-brown mb-2">Attestation de formation</h3>
              <p className="font-body text-sm text-brown-mid mb-4">
                {canDownloadCertificate() 
                  ? 'Félicitations ! Vous avez terminé le parcours. Votre attestation est disponible.' 
                  : 'Complétez les 2 QCM et les 2 évaluations pour débloquer votre attestation.'}
              </p>
              <button
                onClick={downloadCertificate}
                disabled={!canDownloadCertificate()}
                className={`flex items-center gap-2 px-4 py-3 font-body text-sm transition-colors ${
                  canDownloadCertificate()
                    ? 'bg-terra text-white hover:bg-terra-dark'
                    : 'bg-sand text-brown-light cursor-not-allowed'
                }`}
                data-testid="download-certificate-button"
              >
                <Download size={16} />
                Télécharger mon attestation
              </button>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-sand py-8 px-8 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-body text-xs text-brown-light">
            © 2025 L&apos;Académie de l&apos;Immobilier Commercial · Tous droits réservés
          </p>
        </div>
      </footer>
    </div>
  );
}
